import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatInput } from '@angular/material/input';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { copyContentToClipboard } from '../../../../utils/miscellaneous';
import { FeedbackService } from '../../../../core/feedback.service';
import { capitalizeFirstLetter } from '../../../../core/utilities';
import { ValidationErrorKeys } from '../service/validators';
import { EditLocationService, InputValueType } from '../service/edit-location.service';
import { Subject } from 'rxjs';
import { getFullNameForFieldID } from '../../location-utils';
import { memoize } from '../../../../core/decorators/memoize';
import { EditLocationLocalStore } from '../edit-location-local-store.service';
import { takeUntil } from 'rxjs/operators';

// A placeholder component. Will be extended by concrete input components in ./*/editable-inputs.
// Similar in logic to `SharedAddressListColumnComponent`
@Component({
    template: '',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
})
export class SharedEditableInputComponent implements OnInit, OnDestroy {
  // override in inheriting components if needed
  @Input() subType: string;

  // form field ID
  @Input() fieldId: string;

  // for the green datasource dot
  @Input() isFieldWithDataSource: boolean;
  @Input() hasActiveConnection: boolean;

  // mat icon, if applicable
  @Input() iconName?: string;

  // for supporting nested form groups
  // https://stackoverflow.com/questions/44499425/how-to-use-formcontrolname-and-deal-with-nested-formgroup
  @Input() formGroupNameRef?: string | number;

  // the raw material input, if applicable
  @ViewChild(MatInput, { static: false }) matInput?: MatInput;
  // the raw material textarea, if applicable
  @ViewChild('textarea', { static: false }) textarea?: ElementRef<HTMLTextAreaElement>;

  // whether to reserve space for the optional icon and copy + datasource wrappers
  @Input() reservePrefixIconWhitespace?: boolean = true;

  // for determining the `hasBeenEdited` CSS state
  private initialValue: InputValueType = undefined;

  protected componentDestroyed = new Subject<void>();

  constructor(protected cd: ChangeDetectorRef,
              protected feedbackService: FeedbackService,
              protected sidePanelService: EditLocationService,
              protected localStore: EditLocationLocalStore) {
  }

  ngOnInit() {
    // needed for the html util's resolution so that the mat label renders properly
    this.cd.detectChanges();
    this.initialValue = this.actualRawInput?.value;

    this.localStore.newFormGroupNotifier$.pipe(takeUntil(this.componentDestroyed))
        .subscribe(() => this.cd.markForCheck());
  }

  ngOnDestroy() {
    this.componentDestroyed.next();
  }

  /**
   * At most one of these will be present.
   */
  protected get actualRawInput(): HTMLInputElement | HTMLTextAreaElement {
    return (
      // casting to `any` because `_elementRef` shouldn't technically be accessed but
      // we do need the underlying HTML node to call `copyContentToClipboard`, among other things
      ((this.matInput as any)?._elementRef?.nativeElement as HTMLInputElement || this.textarea?.nativeElement)
    );
  }

  /**
   * HTML utils
   */

  /**
   * Returns a "pretty" name for the current field ID.
   */
  @memoize()
  getFieldName(fieldId): string {
    // Capitalize the first letter of a `fieldId` that cannot be resolved.
    // If the field resolves, it'll already have been capitalized properly.
    return capitalizeFirstLetter(getFullNameForFieldID(fieldId),
    );
  }

  /**
   * Dynamically prepare mat-label content.
   */
  getLabel(): string {
    const fieldName = this.getFieldName(this.fieldId);
    return this.hasValue()
        ? fieldName
        : `Add ${fieldName}`;
  }

  getErrorMessage(atFormArrayIndex?: number): null | string {
    // top-level form group controls
    let controls = this.localStore.formGroup.controls as any;
    const isNested = typeof this.formGroupNameRef === 'string' && this.formGroupNameRef.length > 0;

    if (isNested) {
      // account for "nested" form groups
      controls = (controls[this.formGroupNameRef] as UntypedFormGroup).controls;
    }

    if (typeof atFormArrayIndex !== 'undefined') {
      // we're dealing with custom services here so extract the target form group controls
      controls = ((controls.customServices as UntypedFormArray).controls[atFormArrayIndex] as UntypedFormGroup).controls;
    }

    if (!(this.fieldId in controls)) {
      return null;
    }

    const { errors } = controls[this.fieldId];

    const errorKeys: string[] = Object.keys(errors || []);

    // nothing to return..
    if (!errorKeys.length) {
      return null;
    }

    const firstErrorKey = errorKeys[0];

    switch (firstErrorKey) {
      case ValidationErrorKeys.REQUIRED:
        return 'Required field.';
      case ValidationErrorKeys.MIN_LENGTH:
        const min = errors[firstErrorKey].requiredLength;
        return `Minimum of ${min} character(s).`;
      case ValidationErrorKeys.MAX_LENGTH:
        const max = errors[firstErrorKey].requiredLength;
        return `Maximum of ${max} character(s).`;
      case ValidationErrorKeys.CONTAINS_ONLY_WHITESPACE:
        return 'Cannot be whitespace-only.';
      case ValidationErrorKeys.MIN_COORDINATE:
        return 'Below min. value (lat: -90, long: -180)';
      case ValidationErrorKeys.MAX_COORDINATE:
        return 'Above max. value (lat: 90, long: 180)';
      case ValidationErrorKeys.PATTERN_NUMERIC:
        return 'Not a number.';
      default:
        return 'Please check your input.';
    }

  }

  /**
   * Checking the raw input
   */
  getValue(): InputValueType {
    return this.actualRawInput?.value;
  }

  /**
   * Wrapping the private getter.
   */
  hasValue(): boolean {
    return Boolean(this.getValue);
  }

  /**
   * form grp getter
   */
  getFormGroup() {
    return this.localStore.formGroup;
  }

  /**
   * Checking the edited state.
   * @param atFormArrayIndex
   * @param fieldIdOverride
   */
  hasBeenEdited(atFormArrayIndex?: number, fieldIdOverride?: string) {
    const nestedRef = this.formGroupNameRef;

    switch (typeof nestedRef) {
      case 'undefined': {
        // dealing with a non-nested, top-level input
        const isDirty = this.localStore.formGroup.controls[this.fieldId].dirty;
        return isDirty && this.sidePanelService.hasValueTrulyChanged(this.initialValue, this.getValue());
      }
      case 'string': {
        // cover non-array nested groups like `coordinateImport`
        const isDirty = (this.localStore.formGroup.controls[nestedRef] as UntypedFormGroup)?.controls?.[this.fieldId]?.dirty;
        return isDirty && this.sidePanelService.hasValueTrulyChanged(this.initialValue, this.getValue());
      }
      case 'number': {
        // cover array-ish nested groups like `customServices`
        if (typeof atFormArrayIndex === 'undefined' || typeof fieldIdOverride === 'undefined') {
          console.error('Numeric nested-ness is only supported for custom services with defined array indices and field ID overrides.');
          return false;
        }

        const customServArray = this.localStore.formGroup.controls.customServices as UntypedFormArray;
        const customServGroup = customServArray?.controls[atFormArrayIndex] as UntypedFormGroup;
        const targetControl = customServGroup?.controls?.[fieldIdOverride] as UntypedFormControl;

        return targetControl.dirty;
      }
      default:
        return false;
    }

  }

  /**
   * To make LEs' lives easier
   * @param $event the click event
   */
  copyValue($event: MouseEvent) {
    $event.stopPropagation();

    copyContentToClipboard(
        this.actualRawInput,
        (ok) => {
          return ok && this.feedbackService.showSuccessSnackbar({
            data: { userFriendlyMessage: 'Copied!' },
            isAddressListSnackbar: true,
            duration: 3000,
          });
        });
  }
}
